// src/components/auth/LoginModal.tsx
import React, { useState } from 'react';
import {
    Modal,
    Paper,
    Box,
    Avatar,
    Typography,
    TextField,
    Button,
    CircularProgress,
    Alert,
    FormControlLabel,
    Checkbox,
    IconButton,
    useTheme,
    useMediaQuery
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { useAuth } from '../../auth/AuthContext';
import { keyframes } from '@mui/system';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '../../admin/hooks/useTranslation';

const pulse = keyframes`
  0% { transform: scale(1); }
  50% { transform: scale(1.05); }
  100% { transform: scale(1); }
`;

interface LoginModalProps {
    open: boolean;
    onClose: () => void;
    onSwitchToRegister?: () => void;
}

const LoginModal: React.FC<LoginModalProps> = ({ open, onClose, onSwitchToRegister }) => {
    const { t } = useTranslation();
    const { login } = useAuth();
    const [form, setForm] = useState({
        email: '',
        password: '',
        remember: false
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const navigate = useNavigate();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        try {
            await login(form.email, form.password);
            onClose();
            navigate('/profile');

        } catch (err: any) {
            setError(err.response?.data?.message || 'Login failed. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Modal open={open} onClose={onClose}>
            <Paper sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                width: isMobile ? '90%' : 400,
                p: 4,
                borderRadius: 3,
                outline: 'none'
            }}>
                <IconButton
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    onClick={onClose}
                >
                    <CloseIcon />
                </IconButton>

                <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                        src="/logo192.png"
                        sx={{
                            width: 60,
                            height: 60,
                            mx: 'auto',
                            mb: 2,
                            animation: `${pulse} 2s infinite`
                        }}
                    />
                    <Typography variant="h5">{t('login.title', 'Welcome Back')}</Typography>
                    <Typography variant="body2" color="textSecondary">
                        {t('login.subtitle', 'Sign in to your account')}
                    </Typography>
                </Box>

                <form onSubmit={handleSubmit}>
                    <TextField
                        fullWidth
                        label={t('login.email', 'Email')}
                        type="email"
                        margin="normal"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                    <TextField
                        fullWidth
                        label={t('login.password', 'Password')}
                        type="password"
                        margin="normal"
                        value={form.password}
                        onChange={(e) => setForm({ ...form, password: e.target.value })}
                    />
                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.remember}
                                onChange={(e) => setForm({ ...form, remember: e.target.checked })}
                                color="primary"
                            />
                        }
                        label={t('login.remember.me', 'Remember Me')}
                        sx={{ mb: 2 }}
                    />

                    {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

                    <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{ mb: 2 }}
                    >
                        {loading ? <CircularProgress size={24} /> : t('login.singin', 'Sign In')}
                    </Button>

                    <Typography variant="body2" align="center" sx={{ mb: 2 }}>
                        {t('login.register.question', 'Do not have an account?')} {' '}
                        <Button
                            onClick={() => {
                                onClose();
                                onSwitchToRegister?.();
                            }}
                            color="primary"
                            size="small"
                        >
                            {t('login.register', 'Register')}
                        </Button>
                    </Typography>
                </form>
            </Paper>
        </Modal>
    );
};

export default LoginModal;